import CompletedTasksNotification from '@/components/completed-tasks-notification';
import NotificationBanner from '@/components/notification-banner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { UnreadCommentsProvider } from '@/contexts/UnreadCommentsContext';
import { useAuth } from '@/hooks/useAuth';
import { useWebSocket } from '@/hooks/useWebSocket';
import Board from '@/pages/board';
import CalendarPage from '@/pages/calendar';
import CompletedTasks from '@/pages/completed-tasks';
import Dashboard from '@/pages/dashboard';
import HelpRequestsPage from '@/pages/help-requests';
import Login from '@/pages/login';
import NotFound from '@/pages/not-found';
import Projects from '@/pages/projects';
import RecurringTasks from '@/pages/recurring-tasks';
import SettingsPage from '@/pages/settings';
import TaskAnalysis from '@/pages/task-analysis';
import TaskAnalysisDueDate from '@/pages/task-analysis-due-date';
import TeamManagement from '@/pages/team-management';
import { QueryClientProvider } from '@tanstack/react-query';
import { Route, Switch, useLocation } from 'wouter';
import { queryClient } from './lib/queryClient';

function Router() {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  useWebSocket(); // Initialize WebSocket connection

  const handleTaskClick = (taskId: string) => {
    // Store taskId in sessionStorage to persist across navigation
    sessionStorage.setItem('scrollToTaskId', taskId);

    // Navigate to board
    setLocation('/board');

    // Use multiple attempts to find and scroll to the task element
    // This handles cases where the board takes time to render
    let attempts = 0;
    const maxAttempts = 20; // Try for up to 2 seconds

    const scrollToTask = () => {
      attempts++;
      const taskElement = document.querySelector(`[data-task-id="${taskId}"]`);

      if (taskElement) {
        // Remove from sessionStorage since we found it
        sessionStorage.removeItem('scrollToTaskId');

        // Scroll into view
        taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Highlight the task briefly
        taskElement.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50', 'transition-all', 'duration-300');
        setTimeout(() => {
          taskElement.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50', 'transition-all', 'duration-300');
        }, 3000);
      } else if (attempts < maxAttempts) {
        // Try again after a short delay
        setTimeout(scrollToTask, 100);
      } else {
        // Give up after max attempts
        sessionStorage.removeItem('scrollToTaskId');
        console.warn(`Could not find task element with id: ${taskId}`);
      }
    };

    // Start trying to find the element after a brief delay
    setTimeout(scrollToTask, 200);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <Switch>
        {!isAuthenticated ? (
          <Route path="*" component={Login} />
        ) : (
          <>
            <Route path="/" component={Board} />
            <Route path="/board" component={Board} />
            <Route path="/dashboard" component={Dashboard} />
            <Route path="/task-analysis" component={TaskAnalysis} />
            <Route
              path="/task-analysis-due-date"
              component={TaskAnalysisDueDate}
            />
            <Route path="/completed-tasks" component={CompletedTasks} />
            <Route path="/team-management" component={TeamManagement} />
            <Route path="/projects" component={Projects} />
            <Route path="/calendar" component={CalendarPage} />
            <Route path="/recurring-tasks" component={RecurringTasks} />
            <Route path="/settings" component={SettingsPage} />
            <Route path="/help-requests" component={HelpRequestsPage} />
          </>
        )}
        <Route component={NotFound} />
      </Switch>
      {isAuthenticated && <CompletedTasksNotification onTaskClick={handleTaskClick} />}
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <NotificationProvider>
          <UnreadCommentsProvider>
            <Toaster />
            <NotificationBanner />
            <Router />
          </UnreadCommentsProvider>
        </NotificationProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
